package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.internal.concurrent.SyncLock
import top.wcpe.allincore.ui.runtime.internal.concurrent.newWeakKeyedMap
import top.wcpe.allincore.ui.runtime.internal.concurrent.withLock
import top.wcpe.allincore.ui.runtime.internal.time.currentTimeMillis
import kotlin.concurrent.Volatile
import kotlin.jvm.JvmStatic

/**
 * 涟漪动画状态管理器。
 *
 * `InputDispatcher` 在最深命中元素 press 后调用 [start] 注册一条 ripple；
 * `BoxRenderer` / `ButtonRenderer` 渲染时通过 [active] 查询当前活跃 ripple 列表，
 * 按其 progress 调用 `UiRenderBridge.applyRippleEffect`。
 *
 * 每个 [UIElement] 最多保留 [MAX_CONCURRENT] 条并发 ripple，超出时丢弃最早一条。
 * 状态以弱键 Map（[newWeakKeyedMap]）存储，元素被 GC 后条目自动消失（JVM 语义，JS 见 expect KDoc）。
 */
object RippleAnimator {

    /** 涟漪固定时长：启动配置保留但不再控制动画时长。 */
    const val RIPPLE_DURATION_MS = 300L

    /** pressed 状态层默认强度；主题渲染兜底可改用主题 pressed 透明度。 */
    const val RIPPLE_PRESSED_ALPHA = 0.12f

    /** 单元素并发 ripple 上限（与 RFC §3.9.2 一致）。 */
    private const val MAX_CONCURRENT = 3

    /** 元素 → 活跃 ripple 双端队列（队尾为最新）。 */
    private val states: MutableMap<UIElement<*>, ArrayDeque<RippleState>> = newWeakKeyedMap()

    /** [states] 的互斥锁（原 synchronized(states) 监视器语义）。 */
    private val statesLock = SyncLock()

    /** 时钟来源：默认 [currentTimeMillis]，测试可注入。 */
    @Volatile
    private var clock: () -> Long = { currentTimeMillis() }

    /**
     * 触发一条新 ripple。若元素未配置有效半径，则视作不启用 ripple 直接返回。
     * rippleDuration 字段仍保留在 DSL 视觉配置中，但运行期统一按固定 300ms 播放。
     *
     * @param element 宿主元素
     * @param centerX 涟漪圆心相对元素左上角 X（像素）
     * @param centerY 涟漪圆心相对元素左上角 Y（像素）
     */
    @JvmStatic
    fun start(element: UIElement<*>?, centerX: Float, centerY: Float) {
        if (element == null) {
            return
        }
        val visual = element.style().visual()
        val radius = visual.rippleRadius()
        if (radius <= 0f) {
            return
        }
        val state = RippleState(visual.rippleColor(), radius, centerX, centerY, clock(), RIPPLE_DURATION_MS)
        statesLock.withLock {
            val deque = states.getOrPut(element) { ArrayDeque() }
            deque.addLast(state)
            // 并发上限：从队首丢弃最旧
            while (deque.size > MAX_CONCURRENT) {
                deque.removeFirstOrNull()
            }
        }
    }

    /**
     * 返回元素当前的活跃 ripple 列表（progress < 1 的条目），并清理已过期项。
     *
     * @return 不可变的快照列表；无活跃 ripple 时返回空列表
     */
    @JvmStatic
    fun active(element: UIElement<*>?): List<RippleState> {
        if (element == null) {
            return emptyList()
        }
        val now = clock()
        statesLock.withLock {
            val deque = states[element]
            if (deque.isNullOrEmpty()) {
                return emptyList()
            }
            val snapshot = ArrayList<RippleState>(deque.size)
            val it = deque.iterator()
            while (it.hasNext()) {
                val st = it.next()
                val progress = st.progressAt(now)
                if (progress >= 1f) {
                    it.remove()
                } else {
                    snapshot.add(st.withProgress(progress))
                }
            }
            if (deque.isEmpty()) {
                states.remove(element)
            }
            return snapshot
        }
    }

    /** 清空所有元素的 ripple 状态（测试 / Screen 切换时调用）。 */
    @JvmStatic
    fun clearAll() {
        statesLock.withLock {
            states.clear()
        }
    }

    /** 注入时钟（仅供测试 / 平台层模拟）。 */
    @JvmStatic
    fun setClock(supplier: (() -> Long)?) {
        clock = supplier ?: { currentTimeMillis() }
    }

    /** 还原默认时钟。 */
    @JvmStatic
    fun resetClock() {
        clock = { currentTimeMillis() }
    }

    /**
     * 单条涟漪的不可变状态。
     *
     * [progress] 表示几何扩散进度，[alpha] 表示强度衰减。
     * 内部存储仅保留启动时间戳与时长，避免每次 tick 都改写状态。
     *
     * 构造器与 [progressAt]/[withProgress] 为 internal：仅 [RippleAnimator] 同模块构造/驱动，
     * Java 消费方只读公开访问器（`color()` / `radius()` / `progress()` 等）。
     */
    class RippleState internal constructor(
        // 字段名与同名访问器方法冲突，backing 改名加后缀
        private val colorValue: Int,
        private val radiusValue: Float,
        private val centerXValue: Float,
        private val centerYValue: Float,
        private val startMs: Long,
        private val durationMs: Long,
        private val progressValue: Float = 0f,
        private val alphaValue: Float = RippleAnimator.RIPPLE_PRESSED_ALPHA,
    ) {
        fun color(): Int = colorValue
        fun radius(): Float = radiusValue
        fun centerX(): Float = centerXValue
        fun centerY(): Float = centerYValue
        fun progress(): Float = progressValue
        fun alpha(): Float = alphaValue

        internal fun progressAt(now: Long): Float {
            val elapsed = now - startMs
            if (elapsed <= 0L) {
                return 0f
            }
            if (durationMs <= 0L) {
                return 1f
            }
            return (elapsed.toFloat() / durationMs.toFloat()).coerceIn(0f, 1f)
        }

        internal fun withProgress(progress: Float): RippleState = RippleState(
            colorValue,
            radiusValue,
            centerXValue,
            centerYValue,
            startMs,
            durationMs,
            progress,
            RippleAnimator.RIPPLE_PRESSED_ALPHA * (1f - progress),
        )
    }
}
